import { useStore } from '@nanostores/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { workbenchStore } from '~/lib/stores/workbench';
import { streamingFileStore, editingFileStore } from '~/lib/stores/streaming-file';
import type { FileMap } from '~/lib/stores/files';
import { WORK_DIR } from '~/utils/constants';
import { classNames } from '~/utils/classNames';
import { ReadOnlyCodeMirror } from '~/components/editor/codemirror/ReadOnlyCodeMirror';
import { isExportable } from '@dunea/shared/export/workspace-export';
import { resolveViewerTarget } from './code-viewer-target';
import { DownloadCodeButton } from './DownloadCodeButton';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';

/** Strip the WORK_DIR prefix for display. */
function relativePath(fullPath: string): string {
  return fullPath.startsWith(WORK_DIR) ? fullPath.slice(WORK_DIR.length).replace(/^\//, '') : fullPath;
}

/**
 * Read-only code viewer panel. Shows the file the model is currently writing,
 * streamed token-by-token with a typing animation; otherwise shows the file
 * selected in the tree. Editing is intentionally disabled for now (the stores
 * support it, so it can be enabled later without restructuring).
 */
export const CodeViewerPanel = memo(() => {
  const files = useStore(workbenchStore.files);
  const selectedFile = useStore(workbenchStore.selectedFile);
  const currentDocument = useStore(workbenchStore.currentDocument);
  const streaming = useStore(streamingFileStore);
  const editingPath = useStore(editingFileStore);

  const viewerTarget = resolveViewerTarget({ files, selectedFile, currentDocument, streaming });

  const target = viewerTarget?.content ?? '';
  const activePath = viewerTarget?.path ?? '';
  const animate = !!viewerTarget?.animate;
  const errored = !!viewerTarget?.errored;

  // rAF reveal buffer: ease the displayed length toward the target so bursty
  // token chunks read as smooth typing. Static files (tree clicks) show in full.
  const [revealed, setRevealed] = useState('');
  const targetRef = useRef('');
  const animateRef = useRef(false);
  const keyRef = useRef('');
  const resetRef = useRef(false);

  targetRef.current = target;
  animateRef.current = animate;

  useEffect(() => {
    // New file (streaming or selected) → type from the start.
    if (keyRef.current !== activePath) {
      keyRef.current = activePath;
      resetRef.current = true;
    }
  }, [activePath]);

  useEffect(() => {
    let raf = 0;
    let len = 0;

    const loop = () => {
      if (resetRef.current) {
        len = 0;
        resetRef.current = false;
        setRevealed('');
      }

      const full = targetRef.current;

      if (!animateRef.current) {
        if (len !== full.length) {
          len = full.length;
          setRevealed(full);
        }
      } else if (len < full.length) {
        // Reveal faster the further behind we are, so we never lag too far.
        const step = Math.max(2, Math.ceil((full.length - len) / 6));
        len = Math.min(full.length, len + step);
        setRevealed(full.slice(0, len));
      } else if (len > full.length) {
        len = full.length;
        setRevealed(full.slice(0, len));
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(raf);
  }, []);

  const hasContent = viewerTarget !== null;

  // Below md a fixed 224px tree left the code ~130px wide — there it's a slide-over.
  const isNarrow = useIsBelow(BREAKPOINTS.md);
  const [treeOpen, setTreeOpen] = useState(false);

  useEffect(() => {
    setTreeOpen(false);
  }, [selectedFile]);

  const filesButton = isNarrow && (
    <button
      type="button"
      onClick={() => setTreeOpen(true)}
      className="ui-btn-ghost flex-shrink-0 !px-2"
      aria-label="Show files"
    >
      <span className="i-ph:folder-simple text-[13px]" />
      Files
    </button>
  );

  return (
    <div className="relative flex h-full w-full bg-page">
      {(!isNarrow || treeOpen) && (
        <>
          {isNarrow && <div className="absolute inset-0 z-10 bg-black/50" onClick={() => setTreeOpen(false)} />}
          <FileTree
            files={files}
            selectedFile={selectedFile}
            streamingPath={streaming?.status === 'streaming' ? streaming.filePath : (editingPath ?? undefined)}
            className={
              isNarrow
                ? 'absolute inset-y-0 left-0 z-20 w-[min(85%,300px)] bg-surface shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)]'
                : 'w-56 flex-shrink-0'
            }
          />
        </>
      )}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {hasContent ? (
          <>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-hairline flex-shrink-0">
              {filesButton}
              <span className="i-ph:file-code text-[12px] text-muted" />
              <span className="font-geist-mono text-[11px] text-secondary truncate">{relativePath(activePath)}</span>
              {animate && <span className="i-svg-spinners:ring-resize text-accent text-[11px] ml-auto" />}
              {!animate && editingPath === activePath && (
                <span className="ml-auto flex items-center gap-1 text-[11px] text-accent">
                  <span className="i-svg-spinners:ring-resize text-[11px]" />
                  Editing…
                </span>
              )}
              {errored && (
                <span className="ml-auto flex items-center gap-1 text-[11px] text-danger" title={streaming?.errorText}>
                  <span className="i-ph:warning-circle text-[12px]" />
                  Write failed
                </span>
              )}
            </div>
            <div className="flex-1 min-h-0">
              <ReadOnlyCodeMirror content={revealed} filePath={activePath} showCursor={animate} autoScroll={animate} />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center text-muted text-[12px]">
            No file open — the code appears here as it's written.
            {filesButton}
          </div>
        )}
      </div>
    </div>
  );
});

CodeViewerPanel.displayName = 'CodeViewerPanel';

// --- File tree -------------------------------------------------------------

interface TreeNode {
  name: string;
  path: string; // full path
  // Discriminates node kind: a Map (possibly empty) marks a folder; `undefined`
  // marks a file. buildTree upholds this — it only ever assigns a Map to a branch.
  children?: Map<string, TreeNode>;
}

function buildTree(files: FileMap): TreeNode {
  const root: TreeNode = { name: '', path: WORK_DIR, children: new Map() };

  for (const [fullPath, dirent] of Object.entries(files)) {
    if (!dirent || dirent.type !== 'file') {
      continue;
    }

    const rel = relativePath(fullPath);

    // The same definition the download uses (@dunea/shared/export/workspace-export):
    // BASE.md, skills/, start.sh and .dunea/ are the agent's scaffolding, not the
    // user's site. Showing files the "Download code" zip then omits would make the
    // tab a promise the archive breaks.
    if (!rel || !isExportable(rel)) {
      continue;
    }

    const parts = rel.split('/');
    let node = root;

    for (let i = 0; i < parts.length; i++) {
      // An ancestor was already registered as a file (path collision) — skip this entry.
      if (!node.children) {
        break;
      }

      const part = parts[i];
      const isLeaf = i === parts.length - 1;
      const childPath = `${WORK_DIR}/${parts.slice(0, i + 1).join('/')}`;

      if (!node.children.has(part)) {
        node.children.set(part, { name: part, path: childPath, children: isLeaf ? undefined : new Map() });
      }

      node = node.children.get(part)!;
    }
  }

  return root;
}

const FileTree = memo(
  ({
    files,
    selectedFile,
    streamingPath,
    className,
  }: {
    files: FileMap;
    selectedFile?: string;
    streamingPath?: string;
    className?: string;
  }) => {
    const tree = useMemo(() => buildTree(files), [files]);

    return (
      <div className={classNames('border-r border-hairline flex flex-col', className)}>
        {/* Above the tree, not in the editor header: the editor header only
            renders once a file is open, and the download is about the whole
            project, not the file being viewed. */}
        <div className="px-1.5 py-1.5 border-b border-hairline flex-shrink-0">
          <DownloadCodeButton />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto modern-scrollbar py-1.5">
          <TreeChildren node={tree} depth={0} selectedFile={selectedFile} streamingPath={streamingPath} />
        </div>
      </div>
    );
  },
);

FileTree.displayName = 'FileTree';

function TreeChildren({
  node,
  depth,
  selectedFile,
  streamingPath,
}: {
  node: TreeNode;
  depth: number;
  selectedFile?: string;
  streamingPath?: string;
}) {
  if (!node.children) {
    return null;
  }

  // Folders first, then files; each alphabetical.
  const entries = [...node.children.values()].sort((a, b) => {
    const aFolder = !!a.children;
    const bFolder = !!b.children;

    if (aFolder !== bFolder) {
      return aFolder ? -1 : 1;
    }

    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {entries.map((child) => (
        <TreeRow
          key={child.path}
          node={child}
          depth={depth}
          selectedFile={selectedFile}
          streamingPath={streamingPath}
        />
      ))}
    </>
  );
}

function TreeRow({
  node,
  depth,
  selectedFile,
  streamingPath,
}: {
  node: TreeNode;
  depth: number;
  selectedFile?: string;
  streamingPath?: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const isFolder = !!node.children;
  const isSelected = node.path === selectedFile;
  const isStreaming = node.path === streamingPath;

  return (
    <>
      <button
        onClick={() => (isFolder ? setCollapsed((c) => !c) : workbenchStore.setSelectedFile(node.path))}
        className={classNames(
          'flex items-center gap-1.5 w-full text-left px-2 py-1 touch:py-2.5 text-[12px] touch:text-[13px] hover:bg-white/[0.03]',
          isSelected ? 'text-primary bg-white/[0.04]' : 'text-secondary',
        )}
        style={{ paddingLeft: `${8 + depth * 12}px` }}
      >
        <span
          className={classNames(
            'text-[12px] flex-shrink-0',
            isFolder ? (collapsed ? 'i-ph:caret-right' : 'i-ph:caret-down') : 'i-ph:file',
            'text-muted',
          )}
        />
        <span className="truncate">{node.name}</span>
        {isStreaming && <span className="i-svg-spinners:ring-resize text-accent text-[10px] ml-auto flex-shrink-0" />}
      </button>
      {isFolder && !collapsed && (
        <TreeChildren node={node} depth={depth + 1} selectedFile={selectedFile} streamingPath={streamingPath} />
      )}
    </>
  );
}
